<div class="batch-view">
  <lib-pdf-ai-parser-nav></lib-pdf-ai-parser-nav>

  <!-- Header -->
  <header class="page-header">
    <div class="page-header-text">
      <h3>{{ 'AI_PARSER_TITLE' | translate }}</h3>
      <p class="page-subtitle">{{ 'AI_PARSER_INBOX_CAPTION' | translate }}</p>
    </div>
    <!-- Filled inbox (and during initial load): compact panel dropzone -->
    <label
      *ngIf="!(listLoaded && totalItems === 0)"
      class="header-dropzone"
      [class.drag-over]="isDragOver"
      (dragover)="onDragOver($event)"
      (dragleave)="onDragLeave($event)"
      (drop)="onDrop($event)"
    >
      <input
        type="file"
        accept="application/pdf,image/png,image/jpeg,image/gif,image/webp,.pdf,.png,.jpg,.jpeg,.gif,.webp,.xlsx,.xls,.csv"
        multiple
        (change)="onFilesSelected($event)"
        hidden
      />
      <i class="fas fa-arrow-up-from-bracket header-dropzone-icon" aria-hidden="true"></i>
      <span class="header-dropzone-text">
        <span class="header-dropzone-prompt">{{ 'AI_PARSER_DROPZONE_PROMPT' | translate }}</span>
        <span class="header-dropzone-formats">{{ 'AI_PARSER_DROPZONE_FORMATS' | translate }}</span>
      </span>
      <span class="btn btn-primary header-dropzone-cta">{{ 'AI_PARSER_UPLOAD_ORDERS' | translate }}</span>
    </label>

    <!-- Empty inbox: plain button; the large body dropzone leads -->
    <label *ngIf="listLoaded && totalItems === 0" class="btn btn-primary upload-orders-button">
      <i class="fas fa-arrow-up-from-bracket" aria-hidden="true"></i>{{ 'AI_PARSER_UPLOAD_ORDERS' | translate }}
      <input
        type="file"
        accept="application/pdf,image/png,image/jpeg,image/gif,image/webp,.pdf,.png,.jpg,.jpeg,.gif,.webp,.xlsx,.xls,.csv"
        multiple
        (change)="onFilesSelected($event)"
        hidden
      />
    </label>
  </header>

  <!-- Skipped unsupported files (dismissible) -->
  <div *ngIf="showSkippedNote" class="inbox-note inbox-note--muted">
    <span>{{ 'AI_PARSER_FILES_SKIPPED' | translate }}</span>
    <button
      type="button"
      class="btn btn-link btn-sm inbox-note-dismiss"
      [attr.aria-label]="'CANCEL' | translate"
      (click)="dismissSkippedNote()"
    >
      <i class="fas fa-xmark" aria-hidden="true"></i>
    </button>
  </div>

  <!-- Upload success (auto-dismisses) -->
  <div *ngIf="lastUploadedCount > 0" class="inbox-note inbox-note--success">
    <i class="fas fa-check" aria-hidden="true"></i>
    {{
      (lastUploadedCount === 1 ? 'AI_PARSER_FILES_UPLOADED_ONE' : 'AI_PARSER_FILES_UPLOADED')
        | translate: { count: lastUploadedCount }
    }}
  </div>

  <!-- Selected / uploading / failed files -->
  <div *ngIf="selectedFiles.length > 0" class="upload-strip">
    <div class="upload-strip-header">
      <span class="upload-strip-count">
        {{
          (selectedFiles.length === 1 ? 'AI_PARSER_FILES_SELECTED_ONE' : 'AI_PARSER_FILES_SELECTED')
            | translate: { count: selectedFiles.length }
        }}
      </span>
      <button type="button" class="btn btn-link btn-sm text-secondary" (click)="clearSelectedFiles()">
        {{ 'AI_PARSER_CLEAR_ALL' | translate }}
      </button>
    </div>
    <div class="upload-strip-files">
      <lib-pdf-ai-parser-file-row
        *ngFor="let file of selectedFiles; let i = index"
        [fileName]="file.name"
        [fileSize]="file.size"
        [state]="isUploading ? 'uploading' : fileErrors.get(file) ? 'failed' : 'idle'"
        [errorMessage]="fileErrors.get(file)"
      >
        <ng-container ngProjectAs="[file-row-actions]">
          <button
            type="button"
            class="btn btn-link btn-sm file-remove"
            [attr.aria-label]="'AI_PARSER_FILE_REMOVE' | translate"
            (click)="removeFile(i)"
          >
            <i class="fas fa-xmark" aria-hidden="true"></i>
          </button>
        </ng-container>
      </lib-pdf-ai-parser-file-row>
    </div>
    <div *ngIf="fileErrors.size > 0" class="inbox-note inbox-note--danger">
      {{
        (fileErrors.size === 1 ? 'AI_PARSER_FILES_FAILED_ONE' : 'AI_PARSER_FILES_FAILED')
          | translate: { count: fileErrors.size }
      }}
    </div>
    <div class="upload-strip-actions">
      <span class="upload-strip-caption">{{ 'AI_PARSER_MAX_FILES' | translate }}</span>
      <button type="button" class="btn btn-primary" [disabled]="isUploading" (click)="uploadFiles()">
        <span *ngIf="isUploading" class="spinner-border spinner-border-sm me-1"></span>
        {{
          isUploading
            ? ('AI_PARSER_UPLOADING' | translate)
            : ((selectedFiles.length === 1 ? 'AI_PARSER_UPLOAD_PARSE_FILES_ONE' : 'AI_PARSER_UPLOAD_PARSE_FILES')
              | translate: { count: selectedFiles.length })
        }}
      </button>
    </div>
  </div>

  <!-- Order inbox list -->
  <div class="orders-list">
    <!-- Compact toolbar: our own search (with icon) + page size on one row, status
         quick-filter chips below. gung-list's built-in search/filters/batch are off. -->
    <div *ngIf="listLoaded && totalItems > 0" class="inbox-toolbar">
      <div class="inbox-toolbar-row">
        <div class="inbox-search">
          <i class="fas fa-magnifying-glass inbox-search-icon" aria-hidden="true"></i>
          <input
            type="text"
            class="form-control inbox-search-input"
            [(ngModel)]="searchTerm"
            (ngModelChange)="onSearchChange()"
            [placeholder]="'AI_PARSER_SEARCH_PLACEHOLDER' | translate"
          />
        </div>
        <select
          class="form-select inbox-page-size"
          [(ngModel)]="pageSize"
          (ngModelChange)="onPageSizeChange()"
          [attr.aria-label]="'DISPLAY_BATCH_PREFIX' | translate"
        >
          <option *ngFor="let size of pageSizes" [ngValue]="size">
            {{ 'DISPLAY_BATCH_PREFIX' | translate }} {{ size }}
          </option>
        </select>
      </div>
      <div class="status-chips">
        <button
          type="button"
          class="status-chip"
          [class.active]="activeStatus === 'AWAITING_REVIEW'"
          (click)="toggleStatusChip('AWAITING_REVIEW')"
        >
          <span class="chip-dot chip-dot-warning" aria-hidden="true"></span>
          {{ 'AI_PARSER_STAT_NEEDS_REVIEW' | translate }}
          <span class="chip-count">{{ statusCounts['AWAITING_REVIEW'] || 0 }}</span>
        </button>
        <button
          type="button"
          class="status-chip"
          [class.active]="activeStatus === 'AWAITING_CONFIGURATION'"
          (click)="toggleStatusChip('AWAITING_CONFIGURATION')"
        >
          <span class="chip-dot chip-dot-setup" aria-hidden="true"></span>
          {{ 'AI_PARSER_STAT_NEEDS_SETUP' | translate }}
          <span class="chip-count">{{ statusCounts['AWAITING_CONFIGURATION'] || 0 }}</span>
        </button>
        <button
          type="button"
          class="status-chip"
          [class.active]="activeStatus === 'REJECTED'"
          (click)="toggleStatusChip('REJECTED')"
        >
          <span class="chip-dot chip-dot-muted" aria-hidden="true"></span>
          {{ 'AI_PARSER_STAT_REJECTED' | translate }}
          <span class="chip-count">{{ statusCounts['REJECTED'] || 0 }}</span>
        </button>
        <button
          type="button"
          class="status-chip"
          [class.active]="activeStatus === 'ERROR'"
          (click)="toggleStatusChip('ERROR')"
        >
          <span class="chip-dot chip-dot-danger" aria-hidden="true"></span>
          {{ 'AI_PARSER_STAT_FAILED' | translate }}
          <span class="chip-count">{{ statusCounts['ERROR'] || 0 }}</span>
        </button>
        <button
          type="button"
          class="status-chip"
          [class.active]="activeStatus === 'SUBMIT_OUTCOME_UNKNOWN'"
          (click)="toggleStatusChip('SUBMIT_OUTCOME_UNKNOWN')"
        >
          <span class="chip-dot chip-dot-danger" aria-hidden="true"></span>
          {{ 'AI_PARSER_STAT_OUTCOME_UNKNOWN' | translate }}
          <span class="chip-count">{{ statusCounts['SUBMIT_OUTCOME_UNKNOWN'] || 0 }}</span>
        </button>
        <button
          type="button"
          class="status-chip"
          [class.active]="activeStatus === 'DONE'"
          (click)="toggleStatusChip('DONE')"
        >
          <span class="chip-dot chip-dot-success" aria-hidden="true"></span>
          {{ 'AI_PARSER_STAT_SENT' | translate }}
          <span class="chip-count">{{ statusCounts['DONE'] || 0 }}</span>
        </button>
      </div>
    </div>

    <label
      *ngIf="listLoaded && totalItems === 0; else ordersList"
      class="inbox-empty inbox-empty--dropzone"
      [class.drag-over]="isDragOver"
      (dragover)="onDragOver($event)"
      (dragleave)="onDragLeave($event)"
      (drop)="onDrop($event)"
    >
      <input
        type="file"
        accept="application/pdf,image/png,image/jpeg,image/gif,image/webp,.pdf,.png,.jpg,.jpeg,.gif,.webp,.xlsx,.xls,.csv"
        multiple
        (change)="onFilesSelected($event)"
        hidden
      />
      <i class="fas fa-arrow-up-from-bracket inbox-empty-icon" aria-hidden="true"></i>
      <p class="inbox-empty-title">{{ 'AI_PARSER_INBOX_EMPTY_TITLE' | translate }}</p>
      <p class="inbox-empty-body">{{ 'AI_PARSER_INBOX_EMPTY' | translate }}</p>
      <p class="inbox-empty-prompt">
        {{ 'AI_PARSER_DROPZONE_PROMPT' | translate }}
        <span class="inbox-empty-browse">{{ 'AI_PARSER_DROPZONE_BROWSE' | translate }}</span>
      </p>
    </label>
    <ng-template #ordersList>
      <lib-filter-list [configService]="listConfigService"></lib-filter-list>
    </ng-template>
  </div>
</div>
